import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";
import { ExportImportButtons } from "@/components/ExportImportButtons";

export default function ServicePriceList() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", price_npr: 0, remarks: "", active: true };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/service-charges").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/service-charges/${editing.id}`, form);
      else await api.post("/service-charges", form);
      toast.success("Saved"); setOpen(false); setEditing(null); setForm(blank); load();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/service-charges/${id}`); toast.success("Deleted"); load(); };
  const openEdit = (it) => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); };

  return (
    <div className="space-y-5" data-testid="service-prices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Service Price List</h1>
          <p className="text-sm text-slate-500 mt-1">Configurable service charges in NPR — used to auto-fill payment amounts.</p>
        </div>
        <div className="flex gap-2 items-start">
          <ExportImportButtons kind="service_charges" label="Service Rates" onDone={load} />
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
          <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-charge-btn"><Plus size={14} className="mr-1" /> Add Charge</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Service Charge</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="charge-name-input" /></div>
              <div><Label>Price (NPR)</Label><Input type="number" value={form.price_npr} onChange={(e) => setForm({ ...form, price_npr: parseFloat(e.target.value)||0 })} data-testid="charge-price-input" /></div>
              <div><Label>Remarks</Label><Input value={form.remarks||""} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-slate-900 text-white" data-testid="save-charge-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <table className="w-full dense-table">
          <thead className="bg-slate-50 border-b"><tr>
            <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Service / Product</th>
            <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Price (NPR)</th>
            <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Remarks</th>
            <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`charge-row-${c.id}`}>
                <td className="font-medium">{c.name}</td>
                <td className="text-right font-mono font-semibold">{c.price_npr.toLocaleString()}</td>
                <td className="text-slate-500">{c.remarks || "—"}</td>
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Edit size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c.id)}><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
